import OrdersService from '@cabinet/services/api/orders'
import PaymentsService from '@cabinet/services/api/payments'

const STRIPE_JS_URL = 'https://js.stripe.com/v3/'
const STRIPE_JS_TIMEOUT = 15000
const PAYMENT_ELEMENT_TIMEOUT = 20000
const CONFIRM_TIMEOUT = 60000
const CHECK_PAYMENT_TIMEOUT = 60000
const TRACE_STAGE_LIMIT = 10

let stripeJsPromise = null

// short device summary; the server stores the full user agent next to it.
// touch > 0 on "Mac OS X" means an iPad, which reports itself as a Mac
function getDeviceInfo() {
  const ua = navigator.userAgent
  // checked in priority order: Android user agents also contain "Linux"
  const os = ['iPhone', 'iPad', 'Android', 'Windows', 'Mac OS X', 'CrOS', 'Linux']
    .find(name => ua.includes(name)) || 'unknown OS'
  const mobile = navigator.userAgentData ? navigator.userAgentData.mobile : /Mobi|iPhone|Android/.test(ua)
  return [
    `${mobile ? 'mobile' : 'desktop'} ${os}`,
    `viewport ${window.innerWidth}x${window.innerHeight}`,
    `screen ${window.screen.width}x${window.screen.height}@${window.devicePixelRatio}`,
    `touch ${navigator.maxTouchPoints || 0}`,
    navigator.language,
  ].join(', ')
}

// sends checkout failures (level 'error') and events (level 'info') to the server log (pxd_actions_tracker)
function sendClientLog({ cart, payment, stage, message, level }) {
  return PaymentsService.logClientError({
    cart,
    payment,
    stage,
    level,
    device: getDeviceInfo(),
    message: String(message).slice(0, 1000),
    url: window.location.href.slice(0, 500),
  }).catch(() => {})
}

// One checkout attempt (one opening of the payment modal). Every step is logged with
// the attempt id and the seconds since the modal opened, so one attempt reads as a timeline.
export function createCheckoutTrace(cart) {
  const attempt = Math.random().toString(36).slice(2, 10)
  const startedAt = Date.now()
  // form events (focus/blur/changes) can repeat a lot, so each stage is capped per attempt
  const counts = {}
  return {
    payment: null,
    report(stage, message, level = 'error') {
      counts[stage] = (counts[stage] || 0) + 1
      if (counts[stage] > TRACE_STAGE_LIMIT) return Promise.resolve()
      const elapsed = ((Date.now() - startedAt) / 1000).toFixed(1)
      return sendClientLog({
        cart,
        payment: this.payment,
        stage,
        level,
        message: `[attempt ${attempt} +${elapsed}s] ${message}`,
      })
    },
    info(stage, message) {
      return this.report(stage, message, 'info')
    },
  }
}

// Stripe.js must be loaded only once per page, so every caller shares this promise
export function loadStripeJs() {
  if (window.Stripe) return Promise.resolve(window.Stripe)
  if (!stripeJsPromise) {
    stripeJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      const fail = message => {
        clearTimeout(timer)
        script.remove()
        reject(new Error(message))
      }
      const timer = setTimeout(() => fail('Stripe.js load timeout'), STRIPE_JS_TIMEOUT)
      script.onload = () => {
        if (!window.Stripe) return fail('Stripe.js loaded without Stripe global')
        clearTimeout(timer)
        resolve(window.Stripe)
      }
      script.onerror = () => fail('Stripe.js failed to load')
      script.src = STRIPE_JS_URL
      document.head.append(script)
    }).catch(err => {
      // allow the next call to retry from scratch
      stripeJsPromise = null
      throw err
    })
  }
  return stripeJsPromise
}

const options = {
  layout: {
    type: 'accordion',
    defaultCollapsed: false,
    radios: true,
    spacedAccordionItems: false
  }
};

function normalizeCountryToISO2(value) {
  if (!value) return undefined;

  const v = String(value).trim();

  // Якщо вже ISO2
  if (/^[A-Za-z]{2}$/.test(v)) return v.toUpperCase();

  // Мінімальний мапінг під твої кейси
  const map = {
    // Ukraine
    ukraine: "UA",
    україна: "UA",

    // USA
    usa: "US",
    "united states": "US",
    "united states of america": "US",

    // Great Britain
    uk: "GB",
    "great britain": "GB",
    "united kingdom": "GB",

    // Canada
    canada: "CA",
    канада: "CA",
  };

  const key = v.toLowerCase();
  return map[key];
}

export default class clientSecret {
  constructor(summ, key, clientSecret, instance, type, payment, redirect, billing_details, trace) {
    this.summ = summ
    this.stripeKey = key
    this.clientSecret = clientSecret
    this.stripe = null
    this.paymentRequest = null
    this.instance = instance
    this.product = {}
    this.type = type || ""
    this.payment = payment
    this.redirect = redirect
    this.billing_details = billing_details
    const { first_name, last_name, city, country, address_line_1: line1, address_line_2: line2, zipcode: postal_code, subdivision: state,  } = this.billing_details.billing_address
    this.name = `${first_name} ${last_name}`
    this.trace = trace || createCheckoutTrace(instance)
    this.trace.payment = payment
  }
  getId(key) {
    return `${key}`
  }
  loadSecureScript(product) {
    this.product = product
    return loadStripeJs().then(() => this.setPaymentRequest())
  }
  // resolves when the Payment Element is rendered, rejects if it could not be loaded
  loadScript() {
    return loadStripeJs().then(() => this.setCheckout())
  }
  report(stage, message) {
    return this.trace.report(stage, message)
  }
  info(stage, message) {
    return this.trace.info(stage, message)
  }
  checkPayment() {
    if (this.checkPaymentInterval) return
    const startedAt = Date.now()
    const reported = new Set()
    const reportOnce = (stage, message) => {
      if (reported.has(stage)) return
      reported.add(stage)
      this.report(stage, message)
    }
    this.checkPaymentInterval = setInterval(() => {
      if (Date.now() - startedAt > CHECK_PAYMENT_TIMEOUT) {
        reportOnce('check_timeout', `Payment is not paid ${CHECK_PAYMENT_TIMEOUT / 1000}s after successful confirm`)
      }
      OrdersService.getPayment(this.payment).then(({ data }) => {
        if (data.status == "paid" && this.checkPaymentInterval) {
          clearInterval(this.checkPaymentInterval)
          this.checkPaymentInterval = null
          // leaving the page would cancel the log request, so give it a moment first
          const logged = this.info('paid', 'Payment is paid, redirecting to the order confirmation')
          const timeout = new Promise(resolve => setTimeout(resolve, 1500))
          Promise.race([logged, timeout]).then(() => window.location.assign(data.order_redirect))
        }
      }, err => {
        console.log(err);
        reportOnce('check_error', err.response ? `HTTP ${err.response.status}` : err.message)
      })
    }, 2000)
  }
  setPaymentRequest() {
    this.stripe = Stripe(this.stripeKey, {
      apiVersion: "2022-08-01",
    })
    this.paymentRequest = this.stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: this.product.name,
        amount: Number(this.summ * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    })
    
    const form = document.getElementById(this.getId('payment-form'))
    this.paymentRequest.on('paymentmethod', (ev) => {
      form.classList.add('form-loader')
      // Confirm the PaymentIntent without handling potential next actions (yet).
      const { first_name, last_name, city, country, address_line_1: line1, address_line_2: line2, zipcode: postal_code, subdivision: state,  } = this.billing_details.billing_address
      this.stripe.confirmCardPayment(this.clientSecret, {
        payment_method: {
          id: ev.paymentMethod.id,
          billing_details: {
            "address": {
              city,
              country,
              line1,
              line2,
              postal_code,
              state,
            },
            "email": this.billing_details.email,
            "name": this.name,
            "phone": this.billing_details.phone
          },
        }
      }, {handleActions: false}).then(({paymentIntent, error: confirmError}) => {

        if (confirmError) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          ev.complete('fail');
          form.classList.remove('form-loader')
          const messageContainer = document.querySelector(`#${this.getId('error-message')}`)
          messageContainer.textContent = confirmError.message + ' ' + confirmError.decline_code
          OrdersService.updatePaymentError(this.payment, { transaction_status: confirmError.decline_code })
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete('success');
          // Check if the PaymentIntent requires any actions and, if so, let Stripe.js
          // handle the flow. If using an API version older than "2019-02-11"
          // instead check for: `paymentIntent.status === "requires_source_action"`.
          if (paymentIntent.status === "requires_action") {
            // Let Stripe.js handle the rest of the payment flow.
            this.stripe.confirmCardPayment(this.clientSecret).then(function({error}) {
              if (error) {
                form.classList.remove('form-first_nameloader')
                const messageContainer = document.querySelector(`#${this.getId('error-message')}`)
                messageContainer.textContent = error.message + ' ' + error.decline_code
                // The payment failed -- ask your customer for a new payment method.
                OrdersService.updatePaymentError(this.payment, { transaction_status: confirmError.decline_code })
              } else {
                // The payment has succeeded.
                // form.classList.remove('form-loader')
                
                this.checkPayment()
              }

            })
          } else {
            // The payment has succeeded.
            // form.classList.remove('form-loader')
            this.checkPayment()
          }
        }
      })
    });
    this.createPaymentBtns()
  }
  
  createPaymentBtns() {
    setTimeout(() => {
      const form = document.getElementById(this.getId('payment-form'))
      form.classList.add('form-loader')
      this.elements = this.stripe.elements({ clientSecret: this.clientSecret, loader: 'always', locale: 'en' })
      const options = {
        amount: Number(this.summ * 100),
        currency: 'USD',
        paymentMethodTypes: ['affirm'],
        // the country that the end-buyer is in
        countryCode: 'US'
      };
      const prButton = this.elements.create('paymentRequestButton', {
        paymentRequest: this.paymentRequest,
      })
      this.paymentRequest.canMakePayment().then((result) => {
        if (result) {
          prButton.mount(this.getId('#payment-element'))

        } else {
          document.getElementById(this.getId('payment-element')).innerText = 'Apple Pay or Google Pay is not available on this device'
        } 
        form.classList.remove('form-loader')
      })
    }, 1000)
  }
  setCheckout() {
    this.stripe = this.stripe || Stripe(this.stripeKey, {
      apiVersion: "2022-08-01",
    })
    return this.createElements()
  }
  createElements() {
    if (this.paymentElement) this.paymentElement.destroy()
    this.elements = null
    const elements = this.stripe.elements({ clientSecret: this.clientSecret, loader: 'always', locale: 'en' })
    const paymentElement = this.paymentElement = elements.create('payment')
    const ready = new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Payment Element load timeout')), PAYMENT_ELEMENT_TIMEOUT)
      paymentElement.on('ready', () => {
        clearTimeout(timer)
        this.elements = elements
        this.info('element_ready', 'Payment Element is rendered')
        resolve()
      })
      paymentElement.on('loaderror', ({ error }) => {
        clearTimeout(timer)
        reject(new Error(`Payment Element loaderror: ${error.type} ${error.code || ''} ${error.message || ''}`))
      })
    })
    this.trackFormEvents(paymentElement)
    this.info('element_mount', 'Mounting Payment Element')
    paymentElement.mount(this.getId('#payment-element'))
    return ready
    // this.completeWithSaved(this.payment_method)
  }
  formStateText() {
    const { type, complete, empty } = this.formState
    return `method=${type || 'none'} complete=${complete} empty=${empty}`
  }
  // Stripe keeps the fields inside its iframe, so only element-level state is visible:
  // the selected payment method, empty/complete flags, focus and blur. Only transitions are logged.
  trackFormEvents(paymentElement) {
    this.formState = { type: null, complete: false, empty: true }
    paymentElement.on('change', event => {
      const previous = this.formState
      const type = event.value ? event.value.type : null
      this.formState = { type, complete: event.complete, empty: event.empty }
      if (type !== previous.type) {
        this.info('method_selected', `Payment method ${previous.type || 'none'} -> ${type}`)
      }
      if (event.complete !== previous.complete) {
        this.info(event.complete ? 'form_complete' : 'form_incomplete', this.formStateText())
      } else if (event.empty && !previous.empty) {
        this.info('form_cleared', this.formStateText())
      }
    })
    paymentElement.on('focus', () => this.info('form_focus', this.formStateText()))
    paymentElement.on('blur', () => this.info('form_blur', this.formStateText()))
    paymentElement.on('escape', () => this.info('form_escape', this.formStateText()))
  }
  destroy() {
    clearInterval(this.checkPaymentInterval)
    if (this.paymentElement) this.paymentElement.destroy()
  }
  complete() {
    const form = document.getElementById(this.getId('payment-form'))

    // while loading or paying, the .form-loader::after overlay covers the form, so clicks
    // (e.g. on "Pay now") hit the overlay and reach the form itself instead of the button
    form.addEventListener('click', () => {
      if (!form.classList.contains('form-loader')) return
      this.info('click_while_busy', `Click on the loader overlay, ${this.formState ? this.formStateText() : 'form not created'}`)
    })

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      this.info('pay_clicked', `Pay now clicked, ${this.formState ? this.formStateText() : 'form not created'}`)
      if (!this.elements) {
        this.report('submit_ignored', 'Payment Element is not ready yet')
        return
      }
      if (form.classList.contains('form-loader')) {
        this.report('submit_ignored', 'Form is busy: loading or a payment is already in progress')
        return
      }
      form.classList.add('form-loader')

      const watchdog = setTimeout(
        () => this.report('confirm_timeout', `confirmPayment is still pending after ${CONFIRM_TIMEOUT / 1000}s`),
        CONFIRM_TIMEOUT
      )
      // run inside a promise so synchronous exceptions also reach .catch below
      Promise.resolve().then(() => this.stripe.confirmPayment({
        //`Elements` instance that was used to create the Payment Element
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: `${this.billing_details.billing_address.first_name} ${this.billing_details.billing_address.last_name}`,
              email: this.billing_details.email,
              phone: this.billing_details.phone,
              address: {
                line1: this.billing_details.billing_address.address_line_1,
                line2: this.billing_details.billing_address.address_line_2,
                city: this.billing_details.billing_address.city,
                state: this.billing_details.billing_address.subdivision,
                postal_code: this.billing_details.billing_address.zipcode,
                country: normalizeCountryToISO2(this.billing_details.billing_address.country),
              }
            }
          }
        },
        redirect: "if_required"
      })).then((res) => {
        clearTimeout(watchdog)
        if (res.error) {
          // This point will only be reached if there is an immediate error when
          // confirming the payment. Show error to your customer (for example, payment
          // details incomplete)
          form.classList.remove('form-loader')
          const messageContainer = document.querySelector(this.getId('#error-message'))
          messageContainer.textContent = [res.error.message, res.error.decline_code].filter(Boolean).join(' ')
          OrdersService.updatePaymentError(this.payment, { transaction_status: res.error.decline_code })
          const { type, code, decline_code, message } = res.error
          this.report('confirm_error', [type, code, decline_code, message].filter(Boolean).join(' | '))
        } else {
          const intent = res.paymentIntent
          if (intent && intent.status !== 'succeeded') {
            const nextAction = intent.next_action ? intent.next_action.type : 'none'
            this.report('confirm_status', `PaymentIntent ${intent.id} status=${intent.status} next_action=${nextAction}`)
          } else {
            this.info('confirm_succeeded', `PaymentIntent ${intent ? intent.id : '?'} succeeded, waiting for paid status`)
          }
          this.checkPayment()
          // Your customer will be redirected to your `return_url`. For some payment
          // methods like iDEAL, your customer will be redirected to an intermediate
          // site first to authorize the payment, then redirected to the `return_url`.
        }

      }).catch(err => {
        clearTimeout(watchdog)
        form.classList.remove('form-loader')
        document.querySelector(this.getId('#error-message')).textContent = err.message
        this.report('confirm_exception', `${err.name}: ${err.message}`)
      })

    })
  }
  completeWithSaved(payment_method) {
    this.stripe.confirmCardPayment(this.clientSecret, {
      payment_method: payment_method
    }).then(function(result) {
      if (result.error) {
        // Show error to your customer
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          // The payment is complete!
        }
      }
    });
  }

}
